import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

import api from "@/lib/api";
import { PageShell } from "@/components/Layout";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#e2a9f1] focus:outline-none px-4 py-3 text-[#EDEDED] rounded-sm placeholder:text-[#444]";

const FIELDS = [
  { key: "name", label: "Name", required: true, placeholder: "Your name", type: "text" },
  { key: "email", label: "Email", required: true, placeholder: "you@example.com", type: "email" },
  { key: "telegram_username", label: "Telegram Username", required: false, placeholder: "@yourhandle", type: "text" },
  { key: "fab_username", label: "Fab Username", required: false, placeholder: "Your Fab profile name", type: "text" },
  { key: "swinghub_username", label: "SwingHub Username", required: false, placeholder: "Your SwingHub profile name", type: "text" },
];

const empty = {
  name: "",
  email: "",
  telegram_username: "",
  fab_username: "",
  swinghub_username: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fab_username.trim() && !form.swinghub_username.trim()) {
      toast.error("Please provide at least one of Fab or SwingHub username for verification.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received");
    } catch (err) {
      const d = err.response?.data?.detail || "Could not send your message";
      toast.error(typeof d === "string" ? d : "Could not send your message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="px-6 lg:px-12 py-20" data-testid="contact-page">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-mono-overline text-[#e2a9f1] mb-4">Get in touch</div>
            <h1 className="text-5xl md:text-6xl font-serif-display text-[#EDEDED] leading-tight">
              Say hello,
              <br />
              <span className="display-italic text-[#e2a9f1]">discreetly.</span>
            </h1>
            <p className="text-[#A3A3A3] mt-6 leading-relaxed max-w-xl">
              Questions about an evening, a venue, or want to be considered for our private guest
              list? Drop us a line. All submissions are read by the team and replied to within 48
              hours.
            </p>
          </motion.div>

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-12 bg-[#131313] border border-[#e2a9f1]/40 p-10 text-center"
              data-testid="contact-success"
            >
              <CheckCircle size={42} weight="thin" className="text-[#e2a9f1] mx-auto mb-4" />
              <h2 className="text-3xl font-serif-display text-[#EDEDED] mb-3">Thank you.</h2>
              <p className="text-[#A3A3A3] leading-relaxed max-w-md mx-auto">
                Your message is with us. We&rsquo;ll be in touch shortly — discretion observed.
              </p>
              <button
                type="button"
                onClick={() => { setForm(empty); setDone(false); }}
                data-testid="contact-send-another"
                className="mt-8 font-mono-overline text-[#e2a9f1] hover:text-[#efc8f7]"
              >
                Send another message →
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-12 bg-[#131313] border border-[#2A2A2A] p-8 md:p-10 space-y-6"
              data-testid="contact-form"
            >
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                    {f.label}
                    {f.required ? <span className="text-[#e2a9f1] ml-1">*</span> : null}
                  </label>
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.key]}
                    onChange={update(f.key)}
                    placeholder={f.placeholder}
                    className={inputCls}
                    data-testid={`contact-${f.key.replace(/_/g, "-")}-input`}
                  />
                </div>
              ))}

              <div>
                <label className="font-mono-overline text-[#A3A3A3] block mb-2">
                  Message<span className="text-[#e2a9f1] ml-1">*</span>
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="How can we help?"
                  className={inputCls + " resize-y"}
                  data-testid="contact-message-input"
                />
              </div>

              <div className="text-xs text-[#A3A3A3] leading-relaxed border-l-2 border-[#e2a9f1]/40 pl-4">
                Fields marked <span className="text-[#e2a9f1]">*</span> are required. At least one
                of <strong className="text-[#EDEDED]">Fab</strong> or{" "}
                <strong className="text-[#EDEDED]">SwingHub</strong> username must be provided so
                we can verify you within the community.
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm py-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PaperPlaneTilt size={18} weight="thin" />
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}
